/**
 * Centered infinite carousel (transform-based, no clones).
 * Adapted from liquid-product-gallery.
 *
 * Optional peekScale (< 1) shrinks off-center slides for a focus effect.
 */

const SNAP_DURATION_MS = 320
const SNAP_DRAG_RATIO = 0.16

export type CenteredLoopCarousel = {
  goToIndex: (index: number, animate?: boolean) => void
  getIndex: () => number
  destroy: () => void
}

export type CenteredLoopCarouselOptions = {
  viewport: HTMLElement
  track: HTMLElement
  slideSelector?: string
  /** Used when slide margins aren't set in CSS */
  gap?: number
  /**
   * Scale of a fully off-center slide (0–1).
   * 1 = equal size (reviews). ~0.85 = middle card larger (3-panel / gallery).
   */
  peekScale?: number
  /** Fires when the centered slide index changes (not while dragging). */
  onIndexChange?: (index: number) => void
}

const wrapIndex = (index: number, length: number) => {
  if (length <= 0) return 0

  return ((index % length) + length) % length
}

const wrapLocation = (location: number, contentSize: number) => {
  if (contentSize <= 0) return 0

  return ((location % contentSize) + contentSize) % contentSize
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

const getStride = (slide: HTMLElement, fallbackGap: number) => {
  const style = window.getComputedStyle(slide)
  const marginRight = parseFloat(style.marginRight) || 0
  const gap = marginRight > 0 ? marginRight : fallbackGap

  return slide.offsetWidth + gap
}

export const setupCenteredLoopCarousel = ({
  viewport,
  track,
  slideSelector = ':scope > .js-centered-loop-slide',
  gap = 16,
  peekScale = 1,
  onIndexChange
}: CenteredLoopCarouselOptions): CenteredLoopCarousel | null => {
  const slides = Array.from(track.querySelectorAll<HTMLElement>(slideSelector))

  if (!slides.length) return null

  const canLoop = slides.length > 2
  const useScale = peekScale < 1
  let slideSize = slides[0].offsetWidth
  let stride = getStride(slides[0], gap)
  let contentSize = slides.length * stride
  let currentIndex = 0
  let location = 0
  let isDragging = false
  let dragStartX = 0
  let dragStartLocation = 0
  let dragAxis: 'horizontal' | 'vertical' | null = null
  let animFrame = 0
  let touchStartY = 0
  let lastEmittedIndex = -1

  const emitIndexChange = (index: number) => {
    if (index === lastEmittedIndex) return

    lastEmittedIndex = index
    onIndexChange?.(index)
  }

  const measure = () => {
    slideSize = slides[0]?.offsetWidth || slideSize
    stride = getStride(slides[0], gap)
    contentSize = slides.length * stride
  }

  const syncIndexFromLocation = () => {
    currentIndex = wrapIndex(Math.round(location / stride), slides.length)
  }

  const render = () => {
    const viewportCenter = viewport.clientWidth / 2
    const baseTranslate = viewportCenter - slideSize / 2 - location

    track.style.transform = `translate3d(${baseTranslate}px, 0, 0)`

    let closest: HTMLElement | null = null
    let closestDist = Infinity

    slides.forEach((slide, index) => {
      const slideX = index * stride
      let loopOffset = 0

      if (canLoop) {
        const half = contentSize / 2
        let rel = slideX - location

        while (rel > half) {
          rel -= contentSize
          loopOffset -= contentSize
        }

        while (rel < -half) {
          rel += contentSize
          loopOffset += contentSize
        }
      }

      const slideCenterViewport = baseTranslate + slideX + loopOffset + slideSize / 2
      const dist = Math.abs(slideCenterViewport - viewportCenter)

      if (useScale) {
        const t = Math.min(1, dist / slideSize)
        const scale = 1 - t * (1 - peekScale)

        slide.style.transform = `translate3d(${loopOffset}px, 0, 0) scale(${scale})`
      } else {
        slide.style.transform = `translate3d(${loopOffset}px, 0, 0)`
      }

      if (dist < closestDist) {
        closestDist = dist
        closest = slide
      }
    })

    slides.forEach((slide) => {
      slide.classList.toggle('is-centered', slide === closest)
    })

    if (!isDragging && closest) {
      emitIndexChange(slides.indexOf(closest))
    }
  }

  const goToIndex = (index: number, animate = true) => {
    if (animFrame) cancelAnimationFrame(animFrame)

    const length = slides.length
    let target = index * stride

    if (canLoop) {
      const wrappedCurrent = wrapLocation(location, contentSize)
      let delta = target - wrappedCurrent

      if (delta > contentSize / 2) delta -= contentSize
      if (delta < -contentSize / 2) delta += contentSize

      target = location + delta
    } else {
      target = Math.max(0, Math.min(target, (length - 1) * stride))
    }

    if (!animate) {
      location = canLoop ? wrapLocation(target, contentSize) : target
      syncIndexFromLocation()
      render()

      return
    }

    const start = location
    const startTime = performance.now()

    const frame = (now: number) => {
      const progress = Math.min(1, (now - startTime) / SNAP_DURATION_MS)

      location = start + (target - start) * easeOutCubic(progress)
      render()

      if (progress < 1) {
        animFrame = requestAnimationFrame(frame)
      } else {
        location = canLoop ? wrapLocation(target, contentSize) : target
        syncIndexFromLocation()
        render()
        animFrame = 0
      }
    }

    animFrame = requestAnimationFrame(frame)
  }

  const settleToNearest = () => {
    const startIndex = Math.round(dragStartLocation / stride)
    const dragged = location - dragStartLocation
    const threshold = stride * SNAP_DRAG_RATIO

    let targetIndex = startIndex

    if (Math.abs(dragged) >= stride) {
      targetIndex = Math.round(location / stride)
    } else if (dragged > threshold) {
      targetIndex = startIndex + 1
    } else if (dragged < -threshold) {
      targetIndex = startIndex - 1
    }

    goToIndex(targetIndex, true)
  }

  const onPointerDown = (clientX: number) => {
    if (animFrame) cancelAnimationFrame(animFrame)

    isDragging = true
    dragAxis = null
    dragStartX = clientX
    dragStartLocation = location
    viewport.classList.add('is-dragging')
  }

  const onTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0]

    if (!touch) return

    touchStartY = touch.clientY
    onPointerDown(touch.clientX)
  }

  const onTouchMove = (event: TouchEvent) => {
    if (!isDragging) return

    const touch = event.touches[0]

    if (!touch) return

    const diffX = touch.clientX - dragStartX
    const diffY = touch.clientY - touchStartY

    if (dragAxis === null && (Math.abs(diffX) > 5 || Math.abs(diffY) > 5)) {
      dragAxis = Math.abs(diffX) > Math.abs(diffY) ? 'horizontal' : 'vertical'
    }

    if (dragAxis === 'vertical') {
      isDragging = false
      viewport.classList.remove('is-dragging')

      return
    }

    if (dragAxis === 'horizontal') {
      event.preventDefault()
      location = dragStartLocation - diffX
      render()
    }
  }

  const onTouchEnd = () => {
    if (!isDragging && dragAxis !== 'horizontal') {
      dragAxis = null
      viewport.classList.remove('is-dragging')

      return
    }

    isDragging = false
    dragAxis = null
    viewport.classList.remove('is-dragging')
    settleToNearest()
  }

  const onMouseDown = (event: MouseEvent) => {
    event.preventDefault()
    onPointerDown(event.clientX)

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!isDragging) return

      location = dragStartLocation - (moveEvent.clientX - dragStartX)
      render()
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)

      if (!isDragging) return

      isDragging = false
      viewport.classList.remove('is-dragging')
      settleToNearest()
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const onResize = () => {
    measure()
    location = currentIndex * stride
    render()
  }

  viewport.addEventListener('touchstart', onTouchStart, { passive: true })
  viewport.addEventListener('touchmove', onTouchMove, { passive: false })
  viewport.addEventListener('touchend', onTouchEnd)
  viewport.addEventListener('touchcancel', onTouchEnd)
  viewport.addEventListener('mousedown', onMouseDown)
  window.addEventListener('resize', onResize)

  measure()
  goToIndex(0, false)

  return {
    goToIndex: (index: number, animate = true) => goToIndex(index, animate),
    getIndex: () => currentIndex,
    destroy: () => {
      if (animFrame) cancelAnimationFrame(animFrame)
      viewport.removeEventListener('touchstart', onTouchStart)
      viewport.removeEventListener('touchmove', onTouchMove)
      viewport.removeEventListener('touchend', onTouchEnd)
      viewport.removeEventListener('touchcancel', onTouchEnd)
      viewport.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('resize', onResize)
      viewport.classList.remove('is-dragging')
      track.style.transform = ''
      slides.forEach((slide) => {
        slide.style.transform = ''
        slide.classList.remove('is-centered')
      })
    }
  }
}
